import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { format } from 'date-fns';
import { ArrowUpRight, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProviderBadge, SentDateRange } from '@/components/shared/automation-reply-badges';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CopyButton } from '@/modules/simulator/ui/copy-button';
import { useBusinessUnits } from '@/hooks/use-business-units';
import {
	FilterField,
	FiltersPopover,
	SettingsSelect,
	SettingsTableRows,
	StatusTag,
	type SettingsColumn,
	type StatusTone
} from '@/modules/settings';
import { useEscalationLog, useEscalationSummary } from '@/hooks/use-escalation-log';
import { usePermissions } from '@/hooks/use-permissions';
import type { EscalationLogEntry, EscalationLogFilter, EscalationOutcome } from '@/services/escalation-log.service';
import { ASSIGN_TARGET_LABELS, ESCALATION_TYPE_LABELS, type AssignTarget, type EscalationType } from './escalation-policy';
import { ESCALATION_LOG_COLUMNS } from './escalation-log-columns';
import type { EscalationLogFilters, EscalationOutcomeFilter } from './use-escalation-log-filters';

const OUTCOME_TAGS: Record<EscalationOutcome, { label: string; tone: StatusTone }> = {
	sent: { label: 'Sent', tone: 'green' },
	failed: { label: 'Failed', tone: 'red' },
	not_configured: { label: 'Not configured', tone: 'grey' }
};

const STATUS_OPTIONS: { value: EscalationOutcomeFilter; label: string }[] = [
	{ value: 'all', label: 'All' },
	...(Object.keys(OUTCOME_TAGS) as EscalationOutcome[]).map((value) => ({ value, label: OUTCOME_TAGS[value].label }))
];

// Rows written before Assign had targets carry the legacy Assign to User.
const LEGACY_ACTION_LABELS: Record<string, string> = { 'assign-to-human': ESCALATION_TYPE_LABELS.assign, unknown: 'Escalation' };

const actionLabel = (action: string) => ESCALATION_TYPE_LABELS[action as EscalationType] ?? LEGACY_ACTION_LABELS[action] ?? action;

/** What stopped a failed or unconfigured escalation. */
const whyOf = (entry: EscalationLogEntry) =>
	entry.outcome === 'not_configured'
		? `Missing ${entry.missing.join(', ') || 'fields'}: set it on the protocol step, or in Settings › Escalations.`
		: entry.reason;

/** Where to fix an escalation that did not go out: its protocol step, or the instructions. */
function FixLink({ entry }: { entry: EscalationLogEntry }) {
	const { can } = usePermissions();
	const source = entry.source;
	if (!source) return null;
	const to = source.kind === 'protocol' ? `/training/protocols/${encodeURIComponent(source.id)}` : '/training/instructions';
	const place =
		source.kind === 'protocol'
			? [source.name ?? 'the protocol', source.nodeTitle].filter(Boolean).join(' · ')
			: `instruction ${source.name ?? source.id}`;
	// Without access to that page the route would bounce to home; name the place instead.
	if (!can('view', source.kind === 'protocol' ? 'workflow' : 'instructions')) {
		return <p className='text-[12px] text-gray-900'>Configured in {place}</p>;
	}
	return (
		<Link to={to} onClick={(e) => e.stopPropagation()} className='inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:underline'>
			<Wrench className='size-3' />
			Fix in {place}
		</Link>
	);
}

/** Header tools: dates, and the outcome filter (with counts). */
export function EscalationLogTools({ filters }: { filters: EscalationLogFilters }) {
	const counts = useEscalationSummary(filters.range);
	const total = counts ? counts.sent + counts.failed + counts.not_configured : undefined;
	return (
		<>
			<SentDateRange value={filters.days} onChange={filters.setDays} className='w-[240px]' />
			<FiltersPopover activeCount={filters.outcome !== 'all' ? 1 : 0}>
				<FilterField label='Status'>
					<SettingsSelect
						value={filters.outcome}
						onValueChange={(value) => filters.setOutcome(value as EscalationOutcomeFilter)}
						options={STATUS_OPTIONS.map((option) => {
							const count = option.value === 'all' ? total : counts?.[option.value];
							return {
								value: option.value,
								label: (
									<>
										{option.label}
										{count !== undefined && <span className='tabular-nums text-gray-800'>{count}</span>}
									</>
								)
							};
						})}
						aria-label='Status'
					/>
				</FilterField>
			</FiltersPopover>
		</>
	);
}

const ESCALATION_LOG_CELLS: Record<string, (entry: EscalationLogEntry) => ReactNode> = {
	at: (entry) => <span className='truncate tabular-nums'>{format(new Date(entry.at), 'd MMM yyyy, HH:mm')}</span>,
	status: (entry) => <StatusTag tone={OUTCOME_TAGS[entry.outcome].tone}>{OUTCOME_TAGS[entry.outcome].label}</StatusTag>,
	ticket: (entry) =>
		!entry.ticketId ? (
			<span className='text-gray-700'>—</span>
		) : entry.ticketUrl ? (
			<a href={entry.ticketUrl} target='_blank' rel='noreferrer' onClick={(e) => e.stopPropagation()} className='inline-flex items-center gap-0.5 hover:underline'>
				#{entry.ticketId}
				<ArrowUpRight className='size-3' />
			</a>
		) : (
			<span>#{entry.ticketId}</span>
		),
	action: (entry) => <span className='truncate'>{actionLabel(entry.action)}</span>,
	content: (entry) => {
		const why = whyOf(entry);
		return (
			<div className='flex min-w-0 flex-col gap-1'>
				<span className='truncate text-gray-900' title={entry.context ?? undefined}>
					{entry.context || '—'}
				</span>
				{entry.outcome !== 'sent' && (
					<>
						{why && <span className='line-clamp-2 text-[12px] leading-4 text-red-700 wrap-anywhere' title={why}>{why}</span>}
						<FixLink entry={entry} />
					</>
				)}
			</div>
		);
	}
};

const ESCALATION_LOG_TABLE: SettingsColumn<EscalationLogEntry>[] = ESCALATION_LOG_COLUMNS.map((head) => ({
	...head,
	cell: ESCALATION_LOG_CELLS[head.id]
}));

function MetaRow({ label, value, copy }: { label: string; value: ReactNode; copy?: string | null }) {
	return (
		<div className='grid grid-cols-[110px_1fr] items-baseline gap-3 py-2 border-b border-gray-100 last:border-0'>
			<dt className='text-[12px] text-gray-800'>{label}</dt>
			<dd className='flex items-center gap-2 min-w-0 text-[13px] text-ink'>
				<span className='truncate'>{value}</span>
				{copy && <CopyButton text={copy} label={label} />}
			</dd>
		</div>
	);
}

/** The sheet a row opens: the full context, what went wrong, where it was configured, and the ids. */
function EscalationDetails({ entry, brandName }: { entry: EscalationLogEntry; brandName: string | undefined }) {
	const why = whyOf(entry);
	const source = entry.source;
	const place = source
		? source.kind === 'protocol'
			? [source.name ?? 'the protocol', source.nodeTitle].filter(Boolean).join(' · ')
			: `Instruction ${source.name ?? source.id}`
		: null;
	return (
		<div className='flex flex-col gap-5 px-5 pb-6'>
			{entry.outcome !== 'sent' && (
				<section aria-label='Why it did not go out' className='flex flex-col gap-1 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3'>
					<p className='text-[12px] font-medium text-red-700'>{OUTCOME_TAGS[entry.outcome].label}</p>
					{why && <p className='text-[13px] leading-[18px] text-ink wrap-anywhere'>{why}</p>}
					<FixLink entry={entry} />
				</section>
			)}
			<div className='relative rounded-[14px] border border-gray-300 bg-gray-50 px-4 py-3'>
				{entry.context ? (
					<>
						<div className='absolute right-3 top-3'>
							<CopyButton text={entry.context} label='context' />
						</div>
						<p className='whitespace-pre-wrap pr-6 text-[13px] leading-[20px] text-ink'>{entry.context}</p>
					</>
				) : (
					<p className='text-[13px] leading-[20px] text-gray-900'>Kim gave no reason for this escalation.</p>
				)}
			</div>
			<dl>
				<MetaRow
					label='Ticket'
					value={
						entry.ticketId ? (
							entry.ticketUrl ? (
								<a href={entry.ticketUrl} target='_blank' rel='noreferrer' className='inline-flex items-center gap-0.5 font-mono hover:underline'>
									#{entry.ticketId}
									<ArrowUpRight className='size-3' />
								</a>
							) : (
								<span className='font-mono'>#{entry.ticketId}</span>
							)
						) : (
							'—'
						)
					}
					copy={entry.ticketId}
				/>
				<MetaRow label='Helpdesk' value={entry.provider ? <ProviderBadge provider={entry.provider} /> : '—'} />
				<MetaRow label='Channel' value={<span className='capitalize'>{entry.channel ?? '—'}</span>} />
				<MetaRow label='Brand' value={brandName ?? (entry.buId != null ? `Brand ${entry.buId}` : 'Account')} />
				<MetaRow label='Action' value={actionLabel(entry.action)} />
				{entry.target && <MetaRow label='Acted on' value={ASSIGN_TARGET_LABELS[entry.target as AssignTarget] ?? entry.target} />}
				<MetaRow label='Configured in' value={place ?? '—'} />
				<MetaRow label='Ran at' value={format(new Date(entry.at), 'd MMM yyyy, HH:mm:ss')} />
				<MetaRow label='Request ID' value={<span className='font-mono text-[12px]'>{entry.requestId ?? '—'}</span>} copy={entry.requestId} />
				<MetaRow label='Trace ID' value={<span className='font-mono text-[12px]'>{entry.traceId ?? '—'}</span>} copy={entry.traceId} />
			</dl>
		</div>
	);
}

/**
 * Logs › Escalations: every escalation action Kim ran for the brand and how it ended. A failed or
 * unconfigured one says why and links to the protocol step to fix.
 */
export function EscalationLog({ filters }: { filters: EscalationLogFilters }) {
	const filter = useMemo<EscalationLogFilter>(
		() => ({ outcome: filters.outcome === 'all' ? undefined : filters.outcome, ...filters.range }),
		[filters.outcome, filters.range]
	);
	const { entries, isLoading, isError, loadMoreFailed, refetch, hasMore, loadingMore, loadMore } = useEscalationLog(filter);
	const [openId, setOpenId] = useState<string | null>(null);
	const open = entries.find((entry) => entry.id === openId);
	const { businessUnits } = useBusinessUnits();
	const brandNameOf = (buId: number | null) => businessUnits.find((unit) => unit.id === String(buId))?.name;

	// A failed "Load older" keeps the rows already shown; only a failed first page replaces them.
	if (isError && entries.length === 0) {
		return (
			<div className='flex flex-col items-center gap-3 py-12'>
				<p className='text-[13px] text-gray-900'>The escalation log could not be loaded.</p>
				<Button variant='outline' size='sm' onClick={() => void refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	return (
		<div id='escalation-log'>
			<SettingsTableRows
				columns={ESCALATION_LOG_TABLE}
				rows={entries}
				rowKey={(entry) => entry.id}
				isLoading={isLoading}
				onRowClick={(entry) => setOpenId(entry.id)}
				empty={
					filters.outcome !== 'all' || filters.days.startDate
						? 'No escalations match these filters.'
						: 'No escalations yet. When Kim escalates a ticket for this brand, it shows here with how it went. Kept for six months.'
				}
			/>
			{hasMore && (
				<div className='flex justify-center py-4'>
					<Button variant='outline' size='sm' onClick={loadMore} disabled={loadingMore} className='rounded-full'>
						{loadingMore ? 'Loading…' : loadMoreFailed ? 'Could not load more. Retry' : 'Load older escalations'}
					</Button>
				</div>
			)}

			<Sheet open={openId != null} onOpenChange={(isOpen) => !isOpen && setOpenId(null)}>
				<SheetContent className='w-[520px] sm:max-w-[520px] overflow-y-auto'>
					<SheetHeader className='px-5 pt-5'>
						<SheetTitle className='text-[15px]'>{open?.ticketId ? `Escalation on ticket #${open.ticketId}` : 'Escalation'}</SheetTitle>
						<SheetDescription className='text-[12px]'>
							{open ? `${actionLabel(open.action)} · ${OUTCOME_TAGS[open.outcome].label} · ${format(new Date(open.at), 'd MMM yyyy, HH:mm')}` : ''}
						</SheetDescription>
					</SheetHeader>
					{open && <EscalationDetails entry={open} brandName={brandNameOf(open.buId)} />}
				</SheetContent>
			</Sheet>
		</div>
	);
}
